// src/components/common/SideCart.jsx
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { FaTimes, FaTrash, FaPlus, FaMinus, FaShoppingBag } from 'react-icons/fa';
import './SideCart.css';

const SideCart = ({ isOpen, onClose }) => {
  const [cartItems, setCartItems] = useState([]);
  const navigate = useNavigate();

  const loadCart = () => {
    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    setCartItems(cart);
  };

  useEffect(() => {
    loadCart();
    window.addEventListener('cartUpdated', loadCart);
    return () => window.removeEventListener('cartUpdated', loadCart);
  }, []);

  useEffect(() => {
    if (isOpen) loadCart();
  }, [isOpen]);

  const updateQuantity = (id, newQuantity) => {
    if (newQuantity < 1) return;
    const updated = cartItems.map(item => item.id === id ? { ...item, quantity: newQuantity } : item);
    localStorage.setItem('cart', JSON.stringify(updated));
    setCartItems(updated);
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const removeItem = (id) => {
    const updated = cartItems.filter(item => item.id !== id);
    localStorage.setItem('cart', JSON.stringify(updated));
    setCartItems(updated);
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleViewCart = () => {
    onClose();
    navigate('/cart');
  };

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  if (!isOpen) return null;

  // Rendered via a portal directly into document.body — this is the fix.
  // SideCart used to render as a child of <nav className="navbar">, and
  // .navbar.scrolled has backdrop-filter, which creates a new "containing
  // block" for position:fixed descendants. That silently made this drawer's
  // fixed positioning relative to the 72px navbar instead of the viewport
  // once scrolled — a portal sidesteps the issue entirely, regardless of
  // what any ancestor's CSS does now or in the future.
  return createPortal(
    <div className="side-cart-overlay">
      <div className="side-cart-backdrop" onClick={onClose}></div>
      <div className="side-cart-drawer">
        <div className="side-cart-header">
          <h3><FaShoppingBag /> Your Cart ({totalItems})</h3>
          <button className="side-cart-close" onClick={onClose}><FaTimes /></button>
        </div>

        {cartItems.length === 0 ? (
          <div className="side-cart-empty">
            <FaShoppingBag className="side-cart-empty-icon" />
            <p>Your cart is empty.</p>
            <button className="side-cart-continue-btn" onClick={onClose}>Continue Shopping</button>
          </div>
        ) : (
          <>
            <div className="side-cart-items">
              {cartItems.map(item => (
                <div key={item.id} className="side-cart-item">
                  <img src={item.image || 'https://via.placeholder.com/80'} alt={item.name} />
                  <div className="side-cart-item-info">
                    <span className="side-cart-item-name">{item.name}</span>
                    {(item.color || item.size) && (
                      <span className="side-cart-item-variant">
                        {[item.color, item.size ? `Size ${item.size}` : null].filter(Boolean).join(' · ')}
                      </span>
                    )}
                    <span className="side-cart-item-price">PKR {item.price.toLocaleString()}</span>
                    <div className="side-cart-qty">
                      <button onClick={() => updateQuantity(item.id, item.quantity - 1)}><FaMinus /></button>
                      <span>{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.id, item.quantity + 1)}><FaPlus /></button>
                    </div>
                  </div>
                  <button className="side-cart-remove" onClick={() => removeItem(item.id)}>
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>

            <div className="side-cart-footer">
              <div className="side-cart-subtotal-row">
                <span>Subtotal</span>
                <span>PKR {subtotal.toLocaleString()}</span>
              </div>
              <p className="side-cart-note">Shipping and taxes calculated at checkout.</p>
              <div className="side-cart-actions">
                <button className="side-cart-view-btn" onClick={handleViewCart}>View Cart</button>
                <button className="side-cart-checkout-btn" onClick={handleCheckout}>Checkout</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default SideCart;